import type { FC } from 'react'
import {
	Table,
	Tbody,
	Tr,
	Td,
	TableContainer,
	Thead,
	Th,
	Box,
	Text,
} from '@chakra-ui/react'
import type { ChakraProps } from '@chakra-ui/react'
import { useTranslation, Trans } from 'react-i18next'
import { getEnhancedSERPOfficeType } from './enhancementutils'

const STATISTICS_SERP_URLS = ['stockholm'] as const

type StatisticsAreaURL = typeof STATISTICS_SERP_URLS[number]
type OfficeType = 'office' | 'serviced_office'
type RentLevelTableData = {
	[key in StatisticsAreaURL]: {
		[key in OfficeType]: RentLevelData[]
	}
}
type SupplyTrendTableData = {
	[key in StatisticsAreaURL]: {
		[key in OfficeType]: SupplyTrendData[]
	}
}

type RentLevelData = {
	level: string
	avarage: string
	change_quarter: string
	change_year: string
}

type SupplyTrendData = {
	factor: string
	month: string
	change_quarter: string
	change_year: string
}

const RENT_LEVEL_DATA: RentLevelTableData = {
	stockholm: {
		office: [
			{
				level: 'Prisvärda alternativ',
				avarage: '1 240 - 3 360kr/person/mån',
				change_quarter: '-3%',
				change_year: '-9%',
			},
			{
				level: 'Genomsnitt',
				avarage: '4 550kr/person/mån',
				change_quarter: '-7%',
				change_year: '-14%',
			},
			{
				level: 'Premiumlägen',
				avarage: '6 700 - 19 500kr/person/mån',
				change_quarter: '+1%',
				change_year: '-5%',
			},
		],
		serviced_office: [
			{
				level: 'Prisvärda kontorshotell',
				avarage: '184 - 644kr/kvm/mån',
				change_quarter: '+11%',
				change_year: '+2%',
			},
			{
				level: 'Genomsnitt',
				avarage: '1 019kr/kvm/mån',
				change_quarter: '+9%',
				change_year: '-1%',
			},
			{
				level: 'Premiumkontorshotell',
				avarage: '1 655 - 3 794kr/kvm/mån',
				change_quarter: '+6%',
				change_year: '+2%',
			},
		],
	},
}

const SUPPLY_TREND_DATA: SupplyTrendTableData = {
	stockholm: {
		office: [
			{
				factor: 'Vakant kontorsyta',
				month: '26 668m2',
				change_quarter: '-13%',
				change_year: '-35%',
			},
			{
				factor: 'Nytt utbud',
				month: '+2 560m2',
				change_quarter: '+6 731m2',
				change_year: '+34 717m2',
			},
			{
				factor: 'Borttaget utbud',
				month: '0m2',
				change_quarter: '-3 754m2',
				change_year: '-45 885m2',
			},
			{
				factor: 'Vakansgrad',
				month: '22%',
				change_quarter: '-3% (25%)',
				change_year: '-9% (31%)',
			},
		],
		serviced_office: [
			{
				factor: 'Bruttoutbud kontorshotell',
				month: '406 399m2',
				change_quarter: '+1%',
				change_year: '-3%',
			},
			{
				factor: 'Antal kontorshotell',
				month: '406 399m2',
				change_quarter: '+1st',
				change_year: '-11st',
			},
			{
				factor: 'Nya kontorshotell',
				month: '2 560m2',
				change_quarter: '6 731m2',
				change_year: '34 717m2',
			},
			{
				factor: 'Beläggningsgrad',
				month: '78%',
				change_quarter: '+3% (75%)',
				change_year: '+9% (69%)',
			},
		],
	},
}

function getIsValidAreaUrl(url: string | undefined): url is StatisticsAreaURL {
	if (!url) {
		return false
	}
	return STATISTICS_SERP_URLS.includes(
		url.toLocaleLowerCase() as StatisticsAreaURL
	)
}

const RentLevelTable: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType === 'coworking_space'
	) {
		return null
	}
	return (
		<Box {...rest}>
			<Text fontWeight="semibold" color="grey.500">
				{t('Hyresnivåer - så mycket kostar ett kontor i Stockholm')}
			</Text>
			<Text color="grey.500">
				<Trans
					i18nKey={
						'För att ge en rättvis bild av marknaden visar vi hyror i tre nivåer, från prisvärda alternativ till premiumlägen. Detta hjälper dig att snabbt förstå <strong>vad som är en rimlig hyra för ditt kontor</strong> beroende på standard och läge just nu.'
					}
				/>
			</Text>
			<TableContainer maxW={{ base: '90vw', md: '100vw' }} my={4}>
				<Table size="sm">
					<Thead>
						<Tr backgroundColor="blue.50" fontWeight="semibold">
							<Th>{t('Nivå')}</Th>
							<Th>{t('Snitthyra')}</Th>
							<Th>{t('Förändring 3 mån')}</Th>
							<Th>{t('Förändring 12 mån')}</Th>
						</Tr>
					</Thead>
					<Tbody>
						{RENT_LEVEL_DATA[areaUrl][officeType].map((d, i) => (
							<Tr key={i} backgroundColor={i % 2 == 0 ? '' : 'blue.50'}>
								<Td>{t(d.level)}</Td>
								<Td>{t(d.avarage)}</Td>
								<Td>{t(d.change_quarter)}</Td>
								<Td>{t(d.change_year)}</Td>
							</Tr>
						))}
					</Tbody>
				</Table>
			</TableContainer>
			<Text fontSize="sm">
				{t(
					'* Indelningen bygger på hur hyrorna fördelar sig i vår databas: prisvärda alternativ motsvarar de 25 procent billigaste kontoren, genomsnitt står för mitten 50 procent av marknaden och premiumlägen avser de 25 procent dyraste.'
				)}
			</Text>
		</Box>
	)
}

const SupplyTrendTable: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType === 'coworking_space'
	) {
		return null
	}
	return (
		<Box {...rest}>
			<Text fontWeight="semibold" color="grey.500">
				{t('Utbud och trender')}
			</Text>
			<Text color="grey.500">
				{t(
					'Utbudet av kontorsyta har minskat kraftigt under hösten, medan tillförseln av nya ytor fortsatt är svagt i området.'
				)}
			</Text>
			<TableContainer maxW={{ base: '90vw', md: '100vw' }} my={4} {...rest}>
				<Table size="sm">
					<Thead>
						<Tr backgroundColor="blue.50" fontWeight="semibold">
							<Th>{t('Faktor')}</Th>
							<Th>{t('Månad')}</Th>
							<Th>{t('Förändring 3 mån')}</Th>
							<Th>{t('Förändring 12 mån')}</Th>
						</Tr>
					</Thead>
					<Tbody>
						{SUPPLY_TREND_DATA[areaUrl][officeType].map((d, i) => (
							<Tr key={i} backgroundColor={i % 2 == 0 ? '' : 'blue.50'}>
								<Td>{t(d.factor)}</Td>
								<Td>{t(d.month)}</Td>
								<Td>{t(d.change_quarter)}</Td>
								<Td>{t(d.change_year)}</Td>
							</Tr>
						))}
					</Tbody>
				</Table>
			</TableContainer>
			<Text fontSize="sm">
				{t(
					'Källa: Data från yta.se’s egna databas av över 10 000 kontor som med uppdaterade och verifierade uppgifter kring storlek, vakansstatus, pris och tillgänglighet.'
				)}
			</Text>
			<Text fontSize="sm">
				{t('Analys utförd av yta.se. Fel kan förekomma.')}
			</Text>
		</Box>
	)
}

export const Statistics: FC<
	{ pageType: string | undefined; areaUrl: string | undefined } & ChakraProps
> = ({ pageType = '', areaUrl, ...rest }) => {
	const { t } = useTranslation()
	const officeType = getEnhancedSERPOfficeType(pageType, t)
	if (
		!areaUrl ||
		!getIsValidAreaUrl(areaUrl) ||
		!officeType ||
		officeType === 'coworking_space'
	) {
		return null
	}
	return (
		<Box {...rest}>
			<Text fontSize="xl" fontWeight="bold" mb={4}>
				{t(
					'Kontorsmarknaden i Stockholm - hyror, utbud och trender (november 2025)'
				)}
			</Text>
			<Text mb={4} color="grey.500">
				<Trans
					i18nKey={
						'Marknaden för kontor i Stockholm har tagit en ordentlig uppsving under början av hösten 2025. Från ett ugångsläge av väldigt höga vakanser både inom traditionella kontor och coworking så är läget nu ett annat, framförallt att det finns väldigt lite tillgängligt utbud inom coworking. Nedan hittar du aktuella <strong>snitthyror, utbudsdata</strong> och <strong>trender</strong> baserade på över 10 000 verifierade kontor på yta.se.'
					}
				/>
			</Text>
			<RentLevelTable pageType={pageType} areaUrl={areaUrl} />
			<SupplyTrendTable pageType={pageType} areaUrl={areaUrl} mt={4} />
		</Box>
	)
}
